import { Menu, X } from "lucide-react"
import * as React from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { useAuth } from "@/auth/useAuth"
import { BurgerContent } from "./burger-content"
import { Button } from "./button"
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet"
import { cn } from "@/lib/utils"

function Logo({ className }: { className?: string }) {
  return (
    <a href="/" className={cn("block h-10 w-[90px] shrink-0", className)}>
      <img src="/logo.svg" alt="Sherpa" className="size-full object-contain" />
    </a>
  )
}

export function SiteHeader({ className }: { className?: string }) {
  const [open, setOpen] = React.useState(false)
  const menuButtonRef = React.useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isAuthenticated, isMasked, logout } = useAuth()
  const showMaskBanner = isAuthenticated && isMasked

  const handleOutsideInteraction = React.useCallback((e: Event) => {
    const target = e.target as HTMLElement
    if (menuButtonRef.current?.contains(target)) {
      e.preventDefault()
    }
  }, [])

  // The back office opened this tab for the admin; it closes with the session, if the browser lets a script close it.
  const handleExitMask = React.useCallback(async () => {
    await logout()
    window.close()
    navigate("/login", { replace: true })
  }, [logout, navigate])

  return (
    <>
      {showMaskBanner ? (
        <div className="fixed inset-x-0 bottom-0 z-[110] flex items-center justify-center gap-3 bg-amber-500 px-3 py-2 text-center text-sm font-medium text-amber-950">
          <span>{t("maskSession.banner")}</span>
          <button
            type="button"
            className="underline underline-offset-2 hover:no-underline"
            onClick={() => void handleExitMask()}
          >
            {t("maskSession.exit")}
          </button>
        </div>
      ) : null}
      <header className={cn("z-[100] pt-2 sm:pt-4 fixed top-0 bg-transparent h-[48px] sm:h-auto w-full", className)}>
      <div className="mx-auto max-w-[1920px] px-2 sm:px-4">
        <div className="relative z-[100] flex items-center gap-1 bg-card sm:bg-transparent p-1 pl-2 sm:p-0 sm:pl-0 rounded-lg sm:rounded-none">
          <div className="flex grow items-center sm:gap-2 gap-1 rounded-lg bg-card p-1 pl-2 md:gap-9 md:px-[18px] md:py-2.5">
            <Logo className="sm:h-10 h-8 w-auto sm:w-[90px]" />
            <a
              href="tel:+78122070007"
              className="ml-auto hidden text-lg leading-6 text-muted-foreground transition-colors hover:text-foreground lg:inline"
            >
              +7 812 207-00-07
            </a>
          </div>

          <Button onClick={() => navigate("/rent")} variant="primary" size="default" className="w-auto xl:min-w-[230px] hidden sm:inline-flex" showArrow={false}>
            Арендовать бокс
          </Button>
          <Button onClick={() => navigate("/rent")} variant="secondary" className="max-w-[163px] bg-muted inline-flex sm:hidden h-[40px] text-[16px] leading-[24px] rounded-[6px] ml-auto px-4 py-2" showArrow={false}>
            Арендовать бокс
          </Button>
          <Button
            ref={menuButtonRef}
            type="button"
            variant="secondary"
            size="icon"
            className="sm:size-[60px] size-[40px] shrink-0 bg-muted hover:bg-white rounded-[6px] sm:rounded-lg"
            aria-label={open ? "Закрыть меню" : "Открыть меню"}
            aria-expanded={open}
            onClick={() => setOpen((prev) => !prev)}
          >
            {open ? (
              <X className="sm:size-[24px] size-[18px]" />
            ) : (
              <Menu className="sm:size-[24px] size-[18px]" />
            )}
          </Button>
          <Sheet open={open} onOpenChange={setOpen} modal={false}>
            {open
              ? createPortal(
                  <button
                    type="button"
                    aria-label="Закрыть меню"
                    className="fixed inset-0 z-[90] cursor-default bg-[rgba(43,46,52,0.4)] backdrop-blur-sm supports-backdrop-filter:backdrop-blur-md"
                    onClick={() => setOpen(false)}
                  />,
                  document.body,
                )
              : null}
            <SheetContent
              side="right"
              showCloseButton={false}
              onPointerDownOutside={handleOutsideInteraction}
              onInteractOutside={handleOutsideInteraction}
              className="z-[95] flex flex-col gap-0 p-4 pt-20 sm:pt-28 max-md:data-[side=right]:w-full max-md:data-[side=right]:max-w-none"
            >
              <BurgerContent onClose={() => setOpen(false)} />
            </SheetContent>
          </Sheet>

        </div>
      </div>
    </header>
    </>
  )
}
